import { ICornerPanelSource } from "../../../responses/cornerPanelSource";
import { IPanelSource } from "../../../responses/panelSource";
import {
    CustomizedPanel,
    ICategory,
    FacadeElement,
    TypesManagerPanelType,
} from "../../../responses/typesManagerCategories";
import { CornersModelEditor } from "../editors/cornersModelEditor";
import { CustomCornersModelEditor } from "../editors/customCornersModelEditor";
import { CustomPanelsModelEditor } from "../editors/customPanelsModelEditor";
import { CustomZShapedPanelsModelEditor } from "../editors/customZShapedPanelsModelEditor";
import { PanelsModelEditor } from "../editors/panelsModelEditor";
import { Corner } from "../panels/corner";
import { FacadeElementType } from "../panels/facadeElementType";
import { Panel } from "../panels/panel";
import { PanelType } from "../panels/panelType";
import { CategoryType } from "./constants/panelTypeEnums";

type Categories = {
    opaque: { name: CategoryType.Opaque, panelTypes: TypesManagerPanelType[] };
    window: { name: CategoryType.Window, panelTypes: TypesManagerPanelType[] };
    corner: { name: CategoryType.Corner, panelTypes: TypesManagerPanelType[] };
    customPanel: { name: CategoryType.CustomPanel, panelTypes: TypesManagerPanelType[] };
    customCorner: { name: CategoryType.CustomCorner, panelTypes: TypesManagerPanelType[] };
    customZShapedPanel: { name: CategoryType.CustomZShapedPanel, panelTypes: TypesManagerPanelType[] };
};

export class PanelTypeCategories {
    private readonly categories: Categories;
    private selectedPanelTypeId: string | null = null;
    private _selectedPanelId: string | null = null;

    constructor(
        private readonly panelsEditor: PanelsModelEditor,
        private readonly cornersEditor: CornersModelEditor,
        private readonly customPanelsEditor: CustomPanelsModelEditor,
        private readonly customCornersEditor: CustomCornersModelEditor,
        private readonly customZShapedPanelsModelEditor: CustomZShapedPanelsModelEditor,
    ) {
        this.categories = this.initializeCategories();
    }

    public get selectedPanelId(): string | null {
        return this._selectedPanelId;
    }

    public asArray(): ICategory[] {
        return Object.values(this.categories)
            .filter(x => x.panelTypes.length > 0);
    }

    public isCustomCategory(category: CategoryType): boolean {
        return category === CategoryType.CustomPanel ||
            category === CategoryType.CustomCorner ||
            category === CategoryType.CustomZShapedPanel;
    }

    public selectPanel(panelId: string | null) {
        this._selectedPanelId = panelId;
        this.selectPanelType(null);

        if (!panelId) {
            return;
        }

        const panel = this.findRegularPanelById(panelId) ?? this.findCustomPanelById(panelId);
        if (!panel) {
            return;
        }

        const typeId = panel.customPanelTypeId
            ?? (panel.elementType === FacadeElementType.StandardPanel
                || panel.elementType === FacadeElementType.StandardCorner
                ? panel.panelTypeId
                : null);

        this.selectPanelType(typeId);

        return;
    }

    public refresh(): void {
        this.clear();
        this.updatePanels();
    }

    public clear(): void {
        for (const categories of Object.values(this.categories)) {
            categories.panelTypes.length = 0;
        }
    }

    public findReqularPanelTypeById(panelTypeId: string): PanelType<IPanelSource | ICornerPanelSource> | null {
        return this.panelsEditor.findPanelType(panelTypeId)
            ?? this.cornersEditor.findCornerPanelType(panelTypeId)
            ?? null;
    }

    public findPanelTypeById(panelTypeId: string): TypesManagerPanelType | null {
        return this.findReqularPanelTypeById(panelTypeId)
            ?? this.panelsEditor.findConvertedPanelType(panelTypeId)
            ?? this.cornersEditor.findConvertedCornerPanelType(panelTypeId)
            ?? this.customPanelsEditor.findCustomPanelType(panelTypeId)
            ?? this.customCornersEditor.findCustomCornerType(panelTypeId)
            ?? this.customZShapedPanelsModelEditor.findCustomPanelType(panelTypeId)
            ?? null;
    }

    public findRegularPanelsByTypeId(panelTypeId: string): FacadeElement[] {
        const panelType = this.findReqularPanelTypeById(panelTypeId);
        return panelType?.panels || [];
    }

    public findCustomPanelsByTypeId(panelTypeId: string, panelCategory: CategoryType): FacadeElement[] {
        switch (panelCategory) {
            case CategoryType.CustomPanel:
            case CategoryType.CustomCorner: {
                const customPanels = this.getCustomPanelsByCategory(panelCategory, panelTypeId);
                const convertedPanels = this.findConvertedRegularPanels(panelTypeId, panelCategory);
                return [...customPanels, ...convertedPanels];
            }
            case CategoryType.CustomZShapedPanel:
                return this.customZShapedPanelsModelEditor.findCustomZShapedPanelsByTypeId(panelTypeId);
            default:
                return [];
        }
    }

    public findPanelById(panelId: string): FacadeElement | null {
        return this.findRegularPanelById(panelId) ?? this.findCustomPanelById(panelId);
    }

    private initializeCategories(): Categories {
        return {
            opaque: { name: CategoryType.Opaque, panelTypes: [] },
            window: { name: CategoryType.Window, panelTypes: [] },
            corner: { name: CategoryType.Corner, panelTypes: [] },
            customPanel: { name: CategoryType.CustomPanel, panelTypes: [] },
            customCorner: { name: CategoryType.CustomCorner, panelTypes: [] },
            customZShapedPanel: { name: CategoryType.CustomZShapedPanel, panelTypes: [] },
        };
    }

    private selectPanelType(panelTypeId: string | null): void {
        if (this.selectedPanelTypeId === panelTypeId) {
            return;
        }

        if (this.selectedPanelTypeId) {
            const selectedPanelType = this.findPanelTypeById(this.selectedPanelTypeId);
            if (selectedPanelType) {
                selectedPanelType.isSelected = false;
            }
        }

        if (!panelTypeId) {
            this.selectedPanelTypeId = null;
            return;
        }

        const panelType = this.findPanelTypeById(panelTypeId);
        if (!panelType) {
            return;
        }

        panelType.isSelected = true;
        this.selectedPanelTypeId = panelTypeId;
    }

    private findRegularPanelById(panelId: string): Panel | Corner | null {
        const panel = this.panelsEditor.findPanelById(panelId);
        if (panel) {
            return panel;
        }
        const corner = this.cornersEditor.findCornerById(panelId);
        return corner ?? null;
    }

    private findCustomPanelById(panelId: string): CustomizedPanel | null {
        return this.customPanelsEditor.findPanelById(panelId)
            ?? this.customCornersEditor.findCustomCornerById(panelId)
            ?? this.customZShapedPanelsModelEditor.findCustomPanelById(panelId)
            ?? null;
    }

    private updatePanels(): void {
        const opaquePanelTypes = this.panelsEditor.panelTypes
            .filter(type => type.panels.length > 0 && !type.panels[0].hasWindows());

        const windowPanelTypes = this.panelsEditor.panelTypes
            .filter(type => type.panels.length > 0 && type.panels[0].hasWindows());

        const cornerTypes = this.cornersEditor.panelTypes
            .filter(type => type.panels.length > 0);

        const customPanelTypes = [...this.panelsEditor.convertedPanelTypes, ...this.customPanelsEditor.panelTypes]
            .filter(type => type.panels.length > 0);

        const customCornerTypes = [...this.cornersEditor.convertedPanelTypes, ...this.customCornersEditor.panelTypes]
            .filter(type => type.panels.length > 0);

        const customZShapedPanelTypes = this.customZShapedPanelsModelEditor.panelTypes
            .filter(type => type.panels.length > 0);

        this.categories.opaque.panelTypes = opaquePanelTypes;
        this.categories.window.panelTypes = windowPanelTypes;
        this.categories.corner.panelTypes = cornerTypes;
        this.categories.customPanel.panelTypes = customPanelTypes;
        this.categories.customCorner.panelTypes = customCornerTypes;
        this.categories.customZShapedPanel.panelTypes = customZShapedPanelTypes;
    }

    private getCustomPanelsByCategory(category: CategoryType, typeId: string): CustomizedPanel[] {
        switch (category) {
            case CategoryType.CustomPanel:
                return this.customPanelsEditor.findCustomPanelsByTypeId(typeId);
            case CategoryType.CustomCorner:
                return this.customCornersEditor.findCustomCornersByTypeId(typeId);
            default:
                return [];
        }
    }

    private findConvertedRegularPanels(panelTypeId: string, panelCategory: CategoryType): FacadeElement[] {
        switch (panelCategory) {
            case CategoryType.CustomPanel:
                return this.panelsEditor.convertedPanelTypes.filter(x => x.id === panelTypeId)[0]?.panels || [];
            case CategoryType.CustomCorner:
                return this.cornersEditor.convertedPanelTypes.filter(x => x.id === panelTypeId)[0]?.panels || [];
            default:
                return [];
        }
    }
}
